import { Component } from '@theme/component';

/*
 * Bagby alarm demo — plays either a real recorded alarm sound (when a data-audio-url
 * attribute is present, sourced from product.metafields.custom.alarm_audio) or, by default,
 * a soft, escalating synthesized chime via Web Audio on click.
 * Register in snippets/scripts.liquid:
 *   importmap entry:  "@theme/bagby-alarm-demo": "{{ 'bagby-alarm-demo.js' | asset_url }}"
 *   module tag:       <script src="{{ 'bagby-alarm-demo.js' | asset_url }}" type="module" fetchpriority="low"></script>
 */
class BagbyAlarmDemo extends Component {
  constructor() {
    super();
    this.playing = false;
    this.originalTriggerText = '';
    this.ctx = null;
    this.audioUrl = null;
    this.audioEl = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.trigger = (this.refs && this.refs.trigger) || this.querySelector('[ref="trigger"]');
    this.label = (this.refs && this.refs.label) || this.querySelector('[ref="label"]');
    this.eq = (this.refs && this.refs.eq) || this.querySelector('[ref="eq"]');
    if (!this.trigger) return;
    this.originalLabelText = this.label ? this.label.textContent : '';
    this.audioUrl = this.dataset.audioUrl || null;
    this.trigger.addEventListener('click', () => this.handleClick());
  }

  setPlayingState(on) {
    this.playing = on;
    this.classList.toggle('is-playing', on);
    if (this.label) this.label.textContent = on ? 'Playing the alarm…' : this.originalLabelText;
    if (this.eq) this.eq.classList.toggle('is-playing', on);
    if (this.trigger && this.trigger.hasAttribute('aria-label')) {
      this.trigger.setAttribute('aria-label', on ? 'Pause alarm preview' : 'Play alarm preview');
    }
  }

  getContext() {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!this.ctx) {
      try {
        this.ctx = new Ctx();
      } catch (e) {
        return null;
      }
    }
    return this.ctx;
  }

  handleClick() {
    if (this.playing) {
      this.stopPlayback();
      return;
    }
    this.setPlayingState(true);

    if (this.audioUrl) {
      this.playRealAudio();
      return;
    }

    const ctx = this.getContext();
    // Graceful degradation: no Web Audio — animate visuals only, then reset.
    if (!ctx) {
      this.resetTimer = setTimeout(() => this.setPlayingState(false), 3200);
      return;
    }

    try {
      if (ctx.state === 'suspended') ctx.resume();
      this.playAlarm(ctx);
    } catch (e) {
      this.resetTimer = setTimeout(() => this.setPlayingState(false), 3200);
    }
  }

  stopPlayback() {
    if (this.audioEl) this.audioEl.pause();
    this.stopSynth();
    this.setPlayingState(false);
  }

  stopSynth() {
    if (this.activeOscillators) {
      this.activeOscillators.forEach((osc) => {
        try {
          osc.stop();
        } catch (e) {
          // already stopped
        }
      });
      this.activeOscillators = null;
    }
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
  }

  playRealAudio() {
    try {
      if (!this.audioEl) {
        this.audioEl = new Audio(this.audioUrl);
        this.audioEl.addEventListener('ended', () => this.setPlayingState(false));
        this.audioEl.addEventListener('error', () => this.setPlayingState(false));
      }
      this.audioEl.currentTime = 0;
      const playResult = this.audioEl.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => this.setPlayingState(false));
      }
    } catch (e) {
      this.setPlayingState(false);
    }
  }

  playAlarm(ctx) {
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    const noteDur = 0.18;
    const cycles = 3;
    const total = cycles * notes.length;
    let t = ctx.currentTime + 0.02;
    this.activeOscillators = [];

    for (let i = 0; i < total; i++) {
      const cycle = Math.floor(i / notes.length);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = notes[i % notes.length];

      const peak = Math.min(0.15, 0.06 + cycle * 0.045); // escalate gently
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(peak, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + noteDur);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
      };
      osc.start(t);
      osc.stop(t + noteDur);
      this.activeOscillators.push(osc);
      t += noteDur;
    }

    // Reset UI after the full sequence finishes.
    const ms = (t - ctx.currentTime) * 1000 + 120;
    this.resetTimer = setTimeout(() => this.setPlayingState(false), ms);
  }
}

if (!customElements.get('bagby-alarm-demo')) {
  customElements.define('bagby-alarm-demo', BagbyAlarmDemo);
}
